import { getApplicationById } from 'core/api/applications';
import { getReviews } from 'core/api/reviews';
import { useParams } from 'react-router-dom';
import { Review } from 'types';
import StatusCard from 'ui/components/status-card/StatusCard';

import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { getDivisionNameById } from '../details/components/sections/status-info/components/divisions-info/helpers';

function getReasonDataFromReviews(reviews: Review[] | undefined): JSX.Element {
  if (!reviews) {
    return (
      <>
        During the review process this application was denied by one of the
        reviewers. Interaction with this application is no longer available.
      </>
    );
  }

  // find denied review
  const denied = reviews.find((r) => r.state === "rejected");

  if (!denied) {
    return <></>;
  }

  return (
    <>
      During the review process this application was denied by one of the
      reviewers. Interaction with this application is no longer available.
      <br />
      <br />
      Reviewer (Division) -{" "}
      <strong>
        {denied?.reviewerProfile.fullName || ""} (
        {getDivisionNameById(denied.divisionName)})
      </strong>
      <br />
      Denial reason: <br />
      <Box>
        <strong>{denied.rejectionReason || ""}</strong>
      </Box>
    </>
  );
}

export default function ApplicationStateListener() {
  const { id: activeApplicationId = "" } = useParams();
  const applicationQueryKey = ["applications", activeApplicationId];

  const { data } = useQuery({
    queryKey: applicationQueryKey,
    queryFn: () => getApplicationById(activeApplicationId),
    enabled: activeApplicationId !== "",
  });

  const areReviewsNeeded = data?.state === "denied";

  // GET REVIEWS
  const queryKey = ["applications", data?.id || "", "reviews"];
  const { data: reviewsData } = useQuery({
    enabled: data?.id !== "" && Boolean(data?.id) && areReviewsNeeded,
    queryKey: queryKey,
    queryFn: () => getReviews<Review[]>(data?.id || ""),
  });

  if (
    (!reviewsData && areReviewsNeeded) ||
    !data ||
    (data.state !== "denied" && data.state !== "deleted_by_client")
  ) {
    return null;
  }

  const title =
    data.state === "denied"
      ? "This application is denied"
      : "This application is canceled by client";
  const content =
    data.state === "denied"
      ? getReasonDataFromReviews(reviewsData)
      : "Interaction with this application is no longer available.";

  return (
    <Box mt={5}>
      <StatusCard
        title={title}
        content={content}
        isLoading={false}
        status="failed"
      />
    </Box>
  );
}
